import { type FC, Fragment, useState } from 'react'
import classNames from 'classnames'
import { Alert } from 'react-bootstrap'
import { type AccountCustomWorkflowRunSummary, type CustomWorkflowSummary } from '../../../store/customWorkflow/customWorkflows'
import { Spinner } from '../../../components/Spinner/Spinner'
import { type RunRowAction } from './WorkflowRunRow'
import { RunTile } from './RunTile'
import { ChildRunsList } from './ChildRunsList'
import { RunsPager } from './RunsPager'
import { isRunFinished } from '../../../components/CustomWorkflows/runStatus'
import { useTileColumns } from './useTileColumns'
import styles from '../Workflows.module.scss'

interface IndexedRun {
  run: AccountCustomWorkflowRunSummary
  index: number
}

interface RunsTabProps {
  accountId: string
  runs: AccountCustomWorkflowRunSummary[]
  workflowsById: Map<string, CustomWorkflowSummary>
  isLoading: boolean
  isError: boolean
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  emptyMessage: string
  actionFor?: (run: AccountCustomWorkflowRunSummary) => RunRowAction | undefined
  showSubRunBadge?: boolean
  groupChildRuns?: boolean
}

export const RunsTab: FC<RunsTabProps> = ({
  accountId,
  runs,
  workflowsById,
  isLoading,
  isError,
  page,
  totalPages,
  onPageChange,
  emptyMessage,
  actionFor,
  showSubRunBadge,
  groupChildRuns,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null)
  const toggle = (runId: string) => setExpanded((prev) => (prev === runId ? null : runId))
  const columns = useTileColumns()

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }
  if (isError) {
    return <Alert variant='warning'>Could not load your workflow runs.</Alert>
  }
  if (!runs.length) {
    return <div className='py-5 text-center zocks-text-gray-600'>{emptyMessage}</div>
  }

  const rows = runs.reduce<IndexedRun[][]>((rowsSoFar, run, index) => {
    const entry: IndexedRun = { run, index }
    if (index % columns === 0) {
      return [...rowsSoFar, [entry]]
    }
    const lastRow = rowsSoFar[rowsSoFar.length - 1]
    return [...rowsSoFar.slice(0, -1), [...lastRow, entry]]
  }, [])

  return (
    <div className='d-flex flex-column zocks-gap-2'>
      <div className={classNames('d-grid gap-3 align-items-start w-100', styles.tileGrid)}>
        {rows.map((row) => {
          const expandedEntry = groupChildRuns === true ? row.find((entry) => entry.run.id === expanded) : undefined
          return (
            <Fragment key={row[0].run.id}>
              {row.map(({ run, index }) => {
                const isExpanded = groupChildRuns === true && expanded === run.id
                const expandable = groupChildRuns === true && (run.childCount ?? 0) > 0
                return (
                  <RunTile
                    key={run.id}
                    run={run}
                    index={index}
                    workflow={workflowsById.get(run.workflowId)}
                    action={actionFor?.(run)}
                    showSubRunBadge={showSubRunBadge}
                    expanded={isExpanded}
                    className={isExpanded ? styles.tileExpanded : undefined}
                    onToggleExpand={expandable ? () => toggle(run.id) : undefined}
                  />
                )
              })}
              {expandedEntry ? (
                <div
                  key={`${expandedEntry.run.id}-children`}
                  className={classNames('rounded-4 border zocks-border-color-gray-200 bg-white p-3 d-flex flex-column gap-2', styles.tileSpan)}
                  role='region'
                  aria-label={`Sub-runs of ${expandedEntry.run.workflowName || expandedEntry.run.workflowId}`}
                >
                  <div className='paragraph-small medium'>{expandedEntry.run.workflowName || expandedEntry.run.workflowId} · sub-runs</div>
                  <ChildRunsList
                    accountId={accountId}
                    parentRunId={expandedEntry.run.id}
                    parentActive={!isRunFinished(expandedEntry.run.status)}
                    actionFor={actionFor}
                  />
                </div>
              ) : null}
            </Fragment>
          )
        })}
      </div>
      <div className='d-flex justify-content-end pt-2'>
        <RunsPager page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </div>
  )
}
